import * as engine from '../app/engine';
import { app } from '../app/state';
import { PHASE_LABEL } from '../core/constants';
import * as T from '../core/timer';
import { fmtClock } from '../core/util';
import { $ } from './dom';
import { currentLayout, onLayout } from './layout';

/**
 * Strip layout: "pop out" a mini timer that floats above other windows (Document
 * Picture-in-Picture, Chromium only — the button stays hidden elsewhere). The mini window shares
 * this page's state and stylesheets; it ticks on its own timer, so it keeps counting while the
 * main tab is in the background.
 */
interface DocumentPiP {
  requestWindow(opts?: { width?: number; height?: number }): Promise<Window>;
}
const dpip = () => (window as unknown as { documentPictureInPicture?: DocumentPiP }).documentPictureInPicture;
export const pipSupported = () => !!dpip();

const btn = $<HTMLButtonElement>('#popOutBtn');
let pipWin: Window | null = null;

function el<K extends keyof HTMLElementTagNameMap>(doc: Document, tag: K, cls: string) {
  const e = doc.createElement(tag);
  e.className = cls;
  return e;
}

function mirrorRoot(doc: Document) {
  const src = document.documentElement;
  const dst = doc.documentElement;
  for (const k of ['theme', 'type', 'textsize']) {
    const v = src.dataset[k];
    if (v) dst.dataset[k] = v;
  }
  for (const p of ['--text-scale', '--phase']) dst.style.setProperty(p, src.style.getPropertyValue(p));
}

async function open() {
  const api = dpip();
  if (!api || pipWin) return;
  const w = await api.requestWindow({ width: 320, height: 150 });
  pipWin = w;
  const doc = w.document;
  // Same-origin stylesheets only (CSP-safe); in production every style is a <link>.
  for (const n of document.querySelectorAll('link[rel="stylesheet"], style'))
    doc.head.append(n.cloneNode(true));
  doc.title = 'Pomodoro';
  doc.body.className = 'pip';
  const box = el(doc, 'div', 'mini-timer');
  const phase = el(doc, 'span', 'mt-phase');
  const time = el(doc, 'b', 'mt-time');
  const line = el(doc, 'div', 'hz-line');
  const fill = el(doc, 'i', '');
  line.append(fill);
  const toggle = el(doc, 'button', 'mini primary mt-toggle');
  toggle.type = 'button';
  toggle.addEventListener('click', () => engine.toggle());
  box.append(phase, time, line, toggle);
  doc.body.append(box);

  const update = () => {
    const r = app.data.run;
    const now = Date.now();
    mirrorRoot(doc);
    phase.textContent = PHASE_LABEL[r.phase];
    time.textContent = fmtClock(T.remainingAt(r, now));
    fill.style.transform = `scaleX(${(1 - T.fractionLeft(r, now)).toFixed(4)})`;
    toggle.textContent = r.running ? 'PAUSE' : T.inProgress(r) ? 'RESUME' : 'START';
  };
  update();
  const timer = w.setInterval(update, 250);
  w.addEventListener('pagehide', () => {
    w.clearInterval(timer);
    pipWin = null;
    sync();
  });
  sync();
}

function sync() {
  btn.hidden = !pipSupported() || currentLayout() !== 'strip';
  btn.setAttribute('aria-pressed', String(!!pipWin));
}

export function initPip() {
  btn.addEventListener('click', () => (pipWin ? pipWin.close() : void open()));
  onLayout(() => {
    if (currentLayout() !== 'strip') pipWin?.close();
    sync();
  });
  sync();
}
